import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight, IconMapPin } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { WarningEntry, WarningIndicator } from 'components/WarningIndicator';

import { colors } from 'constants/colors';
import { BirthdayDate, formatDateForDisplay } from 'helpers/dateTimes';

/**
 * Un point de la frise : un rangement, une livraison… tout ce qui se répète dans le temps
 * sur un même site. Le libellé et la couleur d'état sont calculés par l'appelant, la frise
 * ne connaît aucun modèle métier.
 */
export type ChronologyEntry = {
    id: string;
    date: Date | BirthdayDate | number;
    /** Couleur du point (état de l'élément). */
    color: string;
    /** Libellé affiché dans la pastille d'état sous la frise. */
    stateLabel: string;
    /** Couleur du texte de la pastille d'état, quand `color` est trop clair pour du blanc. */
    labelColor?: string;
    /** Complément affiché à côté de l'état (livreur, tournée…). */
    meta?: ReactNode;
    /** Fiche de l'élément : la date sélectionnée devient un lien, sauf pour l'élément courant. */
    href?: string;
    /** Alertes signalées par un repère au-dessus du point, détaillées dans son infobulle. */
    warnings?: WarningEntry[];
};

/** Granularité des colonnes de la frise. */
export type ChronologyGrouping = 'week' | 'month';

type ChronologyPeriod = { key: string; title: string; subtitle: string };

// Numéro de semaine ISO 8601 (semaines du lundi au dimanche, la semaine 1 contient le premier jeudi
// de l'année). Calculé à la main car aucun plugin `week` de dayjs n'est chargé dans totadmin.
function getIsoWeekNumber(date: Date): number {
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNumber = (target.getUTCDay() + 6) % 7; // Monday = 0
    target.setUTCDate(target.getUTCDate() - dayNumber + 3); // Thursday of the current week
    const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3);
    return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

/** Colonne « semaine ISO » : du lundi au dimanche. */
function getWeekPeriod(date: dayjs.Dayjs): ChronologyPeriod {
    const dayOfWeek = date.day(); // 0 = Sunday
    const monday = date.add(dayOfWeek === 0 ? -6 : 1 - dayOfWeek, 'day');

    return {
        key: monday.format('YYYY-MM-DD'),
        title: `S${getIsoWeekNumber(monday.toDate())}`,
        subtitle: `${monday.format('DD/MM')} – ${monday.add(6, 'day').format('DD/MM')}`,
    };
}

/** Colonne « mois » : pour des points étalés sur plusieurs mois, où la semaine ne dit plus rien. */
function getMonthPeriod(date: dayjs.Dayjs): ChronologyPeriod {
    const firstDay = date.startOf('month');

    return {
        key: firstDay.format('YYYY-MM'),
        // dayjs n'a pas de locale fr chargée dans totadmin : les noms de mois viennent d'Intl.
        title: firstDay.toDate().toLocaleString('fr-FR', { month: 'short' }),
        subtitle: firstDay.format('YYYY'),
    };
}

/**
 * Frise chronologique navigable : les éléments sont groupés par période (semaine ou mois), l'élément courant
 * est repéré par une épingle, et `renderEntryDetails` permet d'afficher une carte détaillée
 * pour l'élément sélectionné (jamais pour l'élément courant, déjà affiché par la page).
 */
export const Chronology = ({
    entries,
    currentEntryId,
    groupBy = 'week',
    renderEntryDetails,
}: {
    entries: ChronologyEntry[];
    currentEntryId: string;
    /** Semaine pour des éléments hebdomadaires (rangements, livraisons), mois dès que la frise
     *  couvre plusieurs trimestres — sinon les colonnes se multiplient sans rien apprendre. */
    groupBy?: ChronologyGrouping;
    renderEntryDetails?: (entry: ChronologyEntry) => ReactNode;
}) => {
    // Oldest → newest so the rail reads left (passé) → right (présent/futur).
    const sortedEntries = useMemo(
        () => [...entries].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()),
        [entries],
    );

    const currentIndex = sortedEntries.findIndex((entry) => entry.id === currentEntryId);

    // La ligne des repères n'existe que si au moins un point en porte un : les frises sans alerte
    // gardent leur hauteur.
    const hasWarnings = sortedEntries.some(({ warnings }) => !!warnings?.length);

    // Group entries by period (ISO week or month) while keeping each item's global index so the
    // timeline rail can render separated columns without breaking selection/navigation.
    const periodGroups = useMemo(() => {
        const groups: (ChronologyPeriod & { items: { entry: ChronologyEntry; index: number }[] })[] = [];

        sortedEntries.forEach((entry, index) => {
            const date = dayjs(entry.date);
            const period = groupBy === 'month' ? getMonthPeriod(date) : getWeekPeriod(date);

            const lastGroup = groups[groups.length - 1];
            if (lastGroup?.key === period.key) {
                lastGroup.items.push({ entry, index });
                return;
            }

            groups.push({ ...period, items: [{ entry, index }] });
        });

        return groups;
    }, [sortedEntries, groupBy]);

    const initialIndex = Math.max(currentIndex, 0);
    const [selectedIndex, setSelectedIndex] = useState(initialIndex);

    const railRef = useRef<HTMLDivElement>(null);

    // On déplace le scroll du rail lui-même plutôt que d'appeler `scrollIntoView` : ce dernier fait
    // aussi bouger la page autour de la frise.
    const centerNodeInRail = useCallback((index: number, behavior: ScrollBehavior) => {
        const rail = railRef.current;
        const node = rail?.querySelector<HTMLElement>(`[data-node-index="${index}"]`);
        if (!rail || !node) {
            return;
        }

        const railRect = rail.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        rail.scrollTo({
            left: rail.scrollLeft + nodeRect.left - railRect.left - (railRect.width - nodeRect.width) / 2,
            behavior,
        });
    }, []);

    // À l'ouverture, la frise se cale sur l'élément courant : une frise plus large que l'écran
    // s'ouvrirait sinon sur les plus anciens, sans montrer où l'on se situe.
    useEffect(() => {
        centerNodeInRail(initialIndex, 'auto');
    }, [centerNodeInRail, initialIndex]);

    // La frise est souvent plus large que sa colonne, et l'élément courant est le plus à droite :
    // sans ce recentrage au montage, il est coupé par le bord. On déplace le seul `scrollLeft` du
    // rail plutôt que d'appeler `scrollIntoView`, qui ferait aussi défiler la page.
    useEffect(() => {
        const rail = railRef.current;
        const node = rail?.querySelector<HTMLElement>(`[data-node-index="${Math.max(currentIndex, 0)}"]`);

        if (!rail || !node) {
            return;
        }

        const railRect = rail.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        rail.scrollLeft += nodeRect.left - railRect.left - (railRect.width - nodeRect.width) / 2;
    }, [currentIndex]);

    const selectIndex = (index: number) => {
        const clamped = Math.min(Math.max(index, 0), sortedEntries.length - 1);
        setSelectedIndex(clamped);
        centerNodeInRail(clamped, 'smooth');
    };

    const selectedEntry = sortedEntries[selectedIndex];
    const isCurrentSelected = selectedEntry.id === currentEntryId;
    const isFirst = selectedIndex === 0;
    const isLast = selectedIndex === sortedEntries.length - 1;

    return (
        <Wrapper>
            <Rail ref={railRef}>
                <Track>
                    {periodGroups.map((period, periodPosition) => (
                        <Fragment key={period.key}>
                            {periodPosition > 0 ? <PeriodSeparator /> : null}
                            <PeriodColumn>
                                <PeriodLabel>
                                    <PeriodTitle>{period.title}</PeriodTitle>
                                    <PeriodSubtitle>{period.subtitle}</PeriodSubtitle>
                                </PeriodLabel>
                                <PeriodNodes $hasWarnings={hasWarnings}>
                                    {period.items.map(({ entry, index }) => {
                                        const isSelected = index === selectedIndex;
                                        const isCurrent = entry.id === currentEntryId;

                                        return (
                                            <Node
                                                key={entry.id}
                                                type="button"
                                                data-node-index={index}
                                                $isSelected={isSelected}
                                                onClick={() => selectIndex(index)}
                                                title={`${formatDateForDisplay(entry.date, { displayDay: true })} — ${
                                                    entry.stateLabel
                                                }`}
                                            >
                                                {hasWarnings ? (
                                                    <WarningSlot>
                                                        <WarningIndicator
                                                            warnings={entry.warnings ?? []}
                                                            dataTest="chronology-entry-warning"
                                                        />
                                                    </WarningSlot>
                                                ) : null}
                                                <NodeDate $isSelected={isSelected}>
                                                    {dayjs(entry.date).format('DD/MM')}
                                                </NodeDate>
                                                <Dot
                                                    $color={entry.color}
                                                    $isSelected={isSelected}
                                                    $isCurrent={isCurrent}
                                                >
                                                    {isCurrent ? (
                                                        <IconMapPin size={12} color={colors.pureWhite} />
                                                    ) : null}
                                                </Dot>
                                                <NodeTag $isCurrent={isCurrent}>{isCurrent ? 'actuel' : ''}</NodeTag>
                                            </Node>
                                        );
                                    })}
                                </PeriodNodes>
                            </PeriodColumn>
                        </Fragment>
                    ))}
                </Track>
            </Rail>

            <NavRow>
                <NavButton type="button" onClick={() => selectIndex(selectedIndex - 1)} disabled={isFirst}>
                    <IconChevronLeft size={18} />
                    <NavButtonLabel>Précédent</NavButtonLabel>
                </NavButton>

                <NavInfo>
                    <NavDate>
                        {selectedEntry.href && !isCurrentSelected ? (
                            <EntryLink to={selectedEntry.href}>
                                {formatDateForDisplay(selectedEntry.date, { displayDay: true })}
                            </EntryLink>
                        ) : (
                            formatDateForDisplay(selectedEntry.date, { displayDay: true })
                        )}
                        {isCurrentSelected ? <CurrentBadge>actuel</CurrentBadge> : null}
                    </NavDate>
                    <NavMeta>
                        <StateChip $color={selectedEntry.color} $labelColor={selectedEntry.labelColor}>
                            {selectedEntry.stateLabel}
                        </StateChip>
                        <PositionText>
                            n°{selectedIndex + 1} sur {sortedEntries.length}
                        </PositionText>
                        {selectedEntry.meta}
                    </NavMeta>
                </NavInfo>

                <NavButton type="button" onClick={() => selectIndex(selectedIndex + 1)} disabled={isLast}>
                    <NavButtonLabel>Suivant</NavButtonLabel>
                    <IconChevronRight size={18} />
                </NavButton>
            </NavRow>

            {isCurrentSelected || !renderEntryDetails ? null : <CardArea>{renderEntryDetails(selectedEntry)}</CardArea>}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Rail = styled.div`
    overflow-x: auto;
    padding: 4px 0 2px;

    scrollbar-width: thin;
    &::-webkit-scrollbar {
        height: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.lightBorderColor};
        border-radius: 3px;
    }
`;

const Track = styled.div`
    display: flex;
    align-items: stretch;
    /* Mot-clé safe : centré tant que ça tient, aligné à gauche dès que ça déborde — sinon le début
       de la frise passe hors de la zone scrollable et devient inatteignable. */
    justify-content: safe center;
    min-width: 100%;
    padding: 0 8px;
`;

const PeriodSeparator = styled.div`
    flex-shrink: 0;
    align-self: stretch;
    width: 0;
    margin: 2px 6px;
    border-left: 1px dashed ${({ theme }) => theme.lightBorderColor};
`;

const PeriodColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const PeriodLabel = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    white-space: nowrap;
`;

const PeriodTitle = styled.span`
    font-size: 11px;
    font-weight: 800;
    text-transform: capitalize;
    color: ${({ theme }) => theme.textColor};
`;

const PeriodSubtitle = styled.span`
    font-size: 10px;
    color: ${({ theme }) => theme.infoTextColor};
`;

// Hauteur de la ligne des repères d'alerte, écart du flex du `Node` compris : la ligne de la frise
// descend d'autant pour rester alignée sur les points.
const WARNING_SLOT_HEIGHT = 16;
const NODE_GAP = 4;

const PeriodNodes = styled.div<{ $hasWarnings: boolean }>`
    position: relative;
    display: flex;
    align-items: flex-start;

    /* The chronological line behind the dots of this period. */
    &::before {
        content: '';
        position: absolute;
        top: ${({ $hasWarnings }) => 28 + ($hasWarnings ? WARNING_SLOT_HEIGHT + NODE_GAP : 0)}px;
        left: 12px;
        right: 12px;
        height: 2px;
        background: ${({ theme }) => theme.lightBorderColor};
        z-index: 0;
    }
`;

const Node = styled.button<{ $isSelected: boolean }>`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${NODE_GAP}px;
    min-width: 64px;
    padding: 0 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};

    &:hover > div {
        transform: scale(1.15);
    }
`;

const WarningSlot = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${WARNING_SLOT_HEIGHT}px;
`;

const NodeDate = styled.span<{ $isSelected: boolean }>`
    font-size: 11px;
    font-weight: ${({ $isSelected }) => ($isSelected ? 800 : 500)};
    color: ${({ $isSelected, theme }) => ($isSelected ? theme.textColor : theme.infoTextColor)};
    white-space: nowrap;
`;

const Dot = styled.div<{ $color: string; $isSelected: boolean; $isCurrent: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ $isSelected }) => ($isSelected ? '20px' : '14px')};
    height: ${({ $isSelected }) => ($isSelected ? '20px' : '14px')};
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    box-shadow: ${({ $isSelected, $color, theme }) =>
        $isSelected ? `0 0 0 4px ${theme.cardBackgroundColor}, 0 0 0 6px ${$color}` : 'none'};
    outline: ${({ $isCurrent }) => ($isCurrent ? `2px dashed ${colors.blue}` : 'none')};
    outline-offset: 2px;
    transition: all 0.15s ease;
`;

const NodeTag = styled.span<{ $isCurrent: boolean }>`
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-height: 12px;
    color: ${({ $isCurrent }) => ($isCurrent ? colors.blue : 'transparent')};
`;

const NavRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const NavButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 14px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.backgroundColor};
        border-color: ${({ theme }) => theme.darkBorderColor};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const NavButtonLabel = styled.span`
    @media (max-width: 600px) {
        display: none;
    }
`;

const NavInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;
    flex: 1;
    min-width: 0;
`;

const NavDate = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 800;
    text-transform: capitalize;
    color: ${({ theme }) => theme.textColor};
`;

const EntryLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryHoveredColor};

    &:hover {
        text-decoration: underline;
    }
`;

const CurrentBadge = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.pureWhite};
    background-color: ${colors.blue};
    padding: 1px 6px;
    border-radius: 999px;
`;

const NavMeta = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const StateChip = styled.span<{ $color: string; $labelColor?: string }>`
    padding: 1px 8px;
    border-radius: 999px;
    font-weight: 700;
    color: ${({ $labelColor }) => $labelColor ?? colors.pureWhite};
    background-color: ${({ $color }) => $color};
`;

const PositionText = styled.span`
    font-weight: 600;
`;

const CardArea = styled.div``;
